/**
 * Button Component
 *
 * A motion-enhanced button with premium micro-interactions.
 * Features spring-based tap feedback and accessible states.
 *
 * Design Philosophy:
 * - Subtle whileTap scale for tactile feedback
 * - Smooth hover transitions
 * - Respects reduced motion preferences
 * - Full RTL support
 */

import { type FC, forwardRef, type ButtonHTMLAttributes } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useReducedMotion, springPresets } from '@/utils/animations';

// =============================================================================
// Types
// =============================================================================

export interface ButtonProps
  extends Omit<HTMLMotionProps<'button'>, 'children'> {
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Whether button is in loading state */
  loading?: boolean;
  /** Loading text (optional) */
  loadingText?: string;
  /** Icon to display before text */
  leftIcon?: React.ReactNode;
  /** Icon to display after text */
  rightIcon?: React.ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Button content */
  children: React.ReactNode;
}

// =============================================================================
// Variant Styles
// =============================================================================

const variantClasses = {
  primary: cn(
    'bg-primary-600 text-white',
    'hover:bg-primary-700',
    'focus-visible:ring-primary-500/30',
    'shadow-md hover:shadow-lg'
  ),
  secondary: cn(
    'bg-surface-100 text-surface-900',
    'hover:bg-surface-200',
    'focus-visible:ring-surface-500/30'
  ),
  outline: cn(
    'border border-surface-300 text-surface-700',
    'hover:bg-surface-50 hover:border-surface-400',
    'focus-visible:ring-surface-500/30'
  ),
  ghost: cn(
    'text-surface-700',
    'hover:bg-surface-100',
    'focus-visible:ring-surface-500/30'
  ),
  danger: cn(
    'bg-error-600 text-white',
    'hover:bg-error-700',
    'focus-visible:ring-error-500/30',
    'shadow-md hover:shadow-lg'
  ),
};

const sizeClasses = {
  sm: 'text-sm px-3 py-1.5 rounded-lg gap-1.5',
  md: 'text-sm px-4 py-2.5 rounded-xl gap-2',
  lg: 'text-base px-6 py-3 rounded-xl gap-2',
};

const iconSizes = {
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

// =============================================================================
// Button Component
// =============================================================================

/**
 * Button with motion-enhanced feedback
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button variant="primary" onClick={handleClick}>
 *   Save Changes
 * </Button>
 *
 * // Loading state
 * <Button loading loadingText="Saving...">
 *   Save
 * </Button>
 *
 * // With icons
 * <Button leftIcon={<Plus />} rightIcon={<ChevronRight />}>
 *   Add Item
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const prefersReducedMotion = useReducedMotion();
    const isDisabled = disabled || loading;

    // Motion props - disabled for reduced motion preference
    const motionProps = prefersReducedMotion
      ? {}
      : {
          whileTap: { scale: 0.96 },
          transition: springPresets.stiff,
        };

    return (
      <motion.button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center',
          'font-medium',
          'transition-colors duration-200',
          // Focus styles
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          // Disabled styles
          'disabled:opacity-50 disabled:cursor-not-allowed',
          // Touch target
          'touch-target',
          // Variant and size
          variantClasses[variant],
          sizeClasses[size],
          // Full width
          fullWidth && 'w-full',
          // Custom classes
          className
        )}
        {...motionProps}
        {...props}
      >
        {/* Loading spinner */}
        {loading && (
          <Loader2
            className={cn('animate-spin', iconSizes[size])}
            aria-hidden="true"
          />
        )}

        {/* Left icon */}
        {!loading && leftIcon && (
          <span className={iconSizes[size]} aria-hidden="true">
            {leftIcon}
          </span>
        )}

        {/* Button text */}
        <span>{loading && loadingText ? loadingText : children}</span>

        {/* Right icon */}
        {!loading && rightIcon && (
          <span
            className={cn(iconSizes[size], 'rtl:rotate-180')}
            aria-hidden="true"
          >
            {rightIcon}
          </span>
        )}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

// =============================================================================
// Icon Button Variant
// =============================================================================

export interface IconButtonProps
  extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'> {
  /** Icon element */
  icon: React.ReactNode;
  /** Accessible label (required for icon-only buttons) */
  'aria-label': string;
}

/**
 * Icon-only button with motion feedback
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, size = 'md', className, ...props }, ref) => {
    const prefersReducedMotion = useReducedMotion();

    const sizeClasses = {
      sm: 'w-8 h-8 rounded-lg',
      md: 'w-10 h-10 rounded-xl',
      lg: 'w-12 h-12 rounded-xl',
    };

    const iconSizes = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    const motionProps = prefersReducedMotion
      ? {}
      : {
          whileTap: { scale: 0.9 },
          transition: springPresets.stiff,
        };

    return (
      <motion.button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center',
          'transition-colors duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantClasses[props.variant || 'ghost'],
          sizeClasses[size],
          className
        )}
        {...motionProps}
        {...props}
      >
        <span className={iconSizes[size]}>{icon}</span>
      </motion.button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default Button;
